import { styled } from 'baseui'
import { lightTheme } from '@/customTheme'
import {
  COMPACT_MODAL_MEDIA,
  COMPACT_MEDIA_BANNER_STYLE,
  COMPACT_PRIMARY_COLUMN_STYLE,
  ModalCloseButton,
  TwoColumnModalFrame,
  paddingLonghands,
} from '@/components/Modal/ResponsiveModalLayout'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import { KIND } from 'baseui/button'
import { selectUser } from '@/store/slices/user/selectors'
import { useSelector } from 'react-redux'
import { useEffect, useRef } from 'react'
import { useAppDispatch } from '@/store/store'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { useTranslation } from 'react-i18next'
import { customAmplitude } from '@/utils/customAmplitude'
import useModal from '@/hooks/useModal'
import Icons from '@/scenes/Editor/components/Icons'
import { WrapAutoScroll } from '@/components/AutoScroll/AutoScroll'

/** Passed as $style so it merges into WrapAutoScroll's own style object (one rule per property). */
const NEWS_COPY_STYLE = {
  ...paddingLonghands(232, 39, 0, 32),
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  width: '50%',
  justifyContent: 'start',
  [COMPACT_MODAL_MEDIA]: { ...COMPACT_PRIMARY_COLUMN_STYLE, paddingTop: '24px' },
}

const NewsMedia = styled('div', {
  width: '50%',
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  paddingLeft: 0,
  background: 'rgba(249, 249, 249, 1)',
  [COMPACT_MODAL_MEDIA]: COMPACT_MEDIA_BANNER_STYLE,
})

interface WhatsNewTwoColumnProps {
  data: any[]
  onClose: () => void
}

function WhatsNewTwoColumn({ data, onClose }: WhatsNewTwoColumnProps) {
  const user = useSelector(selectUser)
  const { setShowModalType } = useModal()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    return () => {
      video?.pause()
    }
  }, [])

  const onClickPrimaryButton = e => {
    e.preventDefault()
    e.stopPropagation()
    if(!user || !user.userIsPremium) {
      dispatch(
        setOpenModalTryPremium({
          isOpen: true,
          source: 'BtSubscriptionWhatsNew',
          callback: () => {
          }
        })
      )
      customAmplitude('Premium Prompt', {
        Source: 'BtSubscriptionWhatsNew',
        Type: 'Standard',
      })
      customAmplitude('Whats New - Try Premium')
    }
    setShowModalType(null)
  }

  return (
    <TwoColumnModalFrame id="modal-what-new">
      <WrapAutoScroll $style={NEWS_COPY_STYLE}>
        <h2 style={{ margin: 0, ...lightTheme.typography.Small16Bold }}>
          {t('New in Bazaart')}
        </h2>
        <h4
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
            margin: '4px 0 32px 0',
            ...lightTheme.typography.Title32Bold,
            fontFamily: 'ES-Rebond-Grotesque-Classic, Rubik, Roboto',
          }}
        >
          {t(data[0]?.text)}
        </h4>
        <ButtonCustom
          type={SizeButton.LARGE}
          kind={KIND.primary}
          style={{
            position: 'relative',
            ...lightTheme.typography.Small14Semibold,
            color: '#fff',
            margin: 0,
            minWidth: '165px',
            width: 'fit-content'
          }}
          onClick={e => onClickPrimaryButton(e)}
        >
          {user?.userIsPremium ? t('Got it!') : t('Try it free')}
        </ButtonCustom>
      </WrapAutoScroll>
      <NewsMedia>
        <video
          ref={videoRef}
          width="100%"
          height="100%"
          style={{
            objectFit: 'cover',
          }}
          loop
          autoPlay
          muted
          playsInline
          src={data[0].video}
        />
      </NewsMedia>
      <ModalCloseButton
        onClick={e => {
          e.stopPropagation()
          onClose()
        }}
      >
        <Icons.WhiteCloseIcon size={24} />
      </ModalCloseButton>
    </TwoColumnModalFrame>
  )
}

export default WhatsNewTwoColumn
